import type { LucideIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";

export interface EmptyStateProps {
  icon: LucideIcon;
  heading: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

/**
 * docs/ui/design-system.md §14/§17: centered column, icon-xl (48px+) in a
 * muted tone, heading, description capped at ~360px, an optional single
 * action — never more than one competing CTA. Distinct from an error state
 * (design-system.md §18) — an EmptyState explains an absence, not a
 * failure; `ProductGrid`'s error path (features/marketplace) uses its own
 * inline error treatment, not this component.
 */
export function EmptyState({
  icon: Icon,
  heading,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div className={cn("flex flex-col items-center gap-3 px-6 py-12 text-center", className)}>
      <Icon className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
      <div className="space-y-1">
        <h3 className="text-h3 text-foreground">{heading}</h3>
        <p className="mx-auto max-w-[360px] text-body text-muted-foreground">{description}</p>
      </div>
      {action && (
        <Button type="button" variant="outline" onClick={action.onClick} className="mt-2">
          {action.label}
        </Button>
      )}
    </div>
  );
}
